import { memo, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import clsx from "clsx";
import { NumberInput } from "components/common/NumberInput";
import type { NavigationBarProps } from "./types";

export const NavigationBar = memo(
  ({
    selectedIndex,
    total,
    canPrev,
    canNext,
    onPrev,
    onNext,
    onSelectIndex,
    prevLabel,
    nextLabel,
    className,
  }: NavigationBarProps) => {
    const handleNumberChange = useCallback(
      (displayNumber: number) => {
        const clampedIndex = Math.min(
          Math.max(Math.round(displayNumber) - 1, 0),
          total - 1,
        );
        onSelectIndex(clampedIndex);
      },
      [total, onSelectIndex],
    );

    if (selectedIndex < 0) {
      return null;
    }

    return (
      <div
        className={clsx("flex items-center justify-center gap-1", className)}
      >
        <button
          type="button"
          onClick={onPrev}
          disabled={!canPrev}
          className={clsx(
            "h-7 flex items-center gap-0.5 shrink-0 rounded-md transition-colors",
            canPrev
              ? "text-gray-700 hover:text-blue-700"
              : "text-gray-400 cursor-not-allowed",
          )}
        >
          <ChevronLeft size={16} strokeWidth={2.2} />
          {prevLabel && (
            <span className="text-xs font-medium">{prevLabel}</span>
          )}
        </button>
        <div className="flex items-center gap-1 text-xs text-gray-500 font-medium select-none shrink-0">
          <div className="w-12">
            <NumberInput
              value={selectedIndex + 1}
              onChange={handleNumberChange}
              min={1}
              max={total}
              step={1}
              showButtons={false}
              inputMode="numeric"
              className="border-gray-300"
              inputClassName="text-center text-xs font-semibold text-gray-700 py-0.5"
            />
          </div>
          <span className="tabular-nums whitespace-nowrap">/ {total}</span>
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={!canNext}
          className={clsx(
            "h-7 flex items-center gap-0.5 shrink-0 rounded-md transition-colors",
            canNext
              ? "text-gray-700 hover:text-blue-700"
              : "text-gray-400 cursor-not-allowed",
          )}
        >
          {nextLabel && (
            <span className="text-xs font-medium">{nextLabel}</span>
          )}
          <ChevronRight size={16} strokeWidth={2.2} />
        </button>
      </div>
    );
  },
);
